import React, { useState } from 'react';
import { Row } from './table';
import { Checkbox } from '../checkbox';
import { ExpandableRow } from './expandableRow';

export const useSelectableTable = <T,>(data: T[]) => {
    const [selectedDataIndex, setSelectedDataIndex] = useState<number[]>([]);
    const mapRows = <Z,>(rows: Z[], fn: (x: Z) => React.ReactNode[], onRowClick?: (x: Z) => void, fnDisabled?: (x: Z) => boolean) =>
        rows.map((row, i) => {
            const isSelected = selectedDataIndex.includes(i);
            const isDisabled = fnDisabled ? fnDisabled(row) : false;

            return (
                <Row
                    selected={isSelected}
                    disabled={isDisabled}
                    key={i.toString()}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    cells={[
                        <Checkbox
                            disabled={isDisabled}
                            checked={isSelected}
                            onChange={() => {
                                if (isSelected) {
                                    setSelectedDataIndex(selectedDataIndex.filter((x) => x !== i));
                                } else {
                                    setSelectedDataIndex([i, ...selectedDataIndex]);
                                }
                            }}
                        />,
                        ...fn(row)
                    ]}
                />
            );
        });

    const mapExpandableRows = <Z,>(
        rows: Z[],
        fn: (x: Z) => React.ReactNode[],
        fnExpandableContent: (x: Z) => React.ReactNode,
        fnOnExpandCallback: (x: Z) => (isExpanded: boolean) => void,
        fnIsExpandable: (x: Z) => boolean,
        fnCheckboxDisabled?: (x: Z) => boolean,
        fnRowKey?: (x: Z, i: number) => React.Key
    ) =>
        rows.map((row, i) => {
            const isSelected = selectedDataIndex.includes(i);

            return (
                <ExpandableRow
                    isExpandable={fnIsExpandable(row)}
                    content={fnExpandableContent(row)}
                    onExpand={fnOnExpandCallback(row)}
                    selected={isSelected}
                    key={fnRowKey ? fnRowKey(row, i) : i.toString()}
                    cells={[
                        <Checkbox
                            disabled={fnCheckboxDisabled ? fnCheckboxDisabled(row) : false}
                            checked={isSelected}
                            onChange={() => {
                                if (isSelected) {
                                    setSelectedDataIndex(selectedDataIndex.filter((x) => x !== i));
                                } else {
                                    setSelectedDataIndex([i, ...selectedDataIndex]);
                                }
                            }}
                        />,
                        ...fn(row)
                    ]}
                />
            );
        });

    const selectedData = selectedDataIndex.map((i) => data[i]);

    const isAllSelected = data.length === selectedDataIndex.length;

    const headingCheck = (
        <Checkbox
            checked={isAllSelected}
            onChange={() => {
                if (isAllSelected) {
                    setSelectedDataIndex([]);
                } else {
                    setSelectedDataIndex(data.map((_, i) => i));
                }
            }}
        />
    );

    const resetSelection = () => setSelectedDataIndex([]);

    return {
        selectedData,
        mapRows,
        mapExpandableRows,
        headingCheck,
        resetSelection
    };
};
